import React, { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../config/api";
import webSocketManager from "../utils/websocketUtils";
import Logo from "../assets/Images/Display_Top_Banner.webp";
import Metal1 from "../assets/Images/metal-1.png";
import Metal2 from "../assets/Images/metal-2.png";
import Metal3 from "../assets/Images/metal-3.png";
import "./Home.css";
const Home = ({ displaySettings }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    fetchTeams();
  }, [displaySettings]);

  // WebSocket connection
  useEffect(() => {
    const socket = webSocketManager.connect(
      "https://api-third-shot-score-684526581556.asia-south1.run.app"
      // "https://api-third-shot-score-684526581556.asia-south1.run.app"
    );

    // Connection event handlers
    const handleConnect = () => {
      console.log("✅ Home WebSocket connected successfully");
      // Join live score room for real-time updates
      socket.emit("joinLiveScore");
    };

    const handleDisconnect = (reason) => {
      console.log("❌ Home WebSocket disconnected:", reason);
    };

    const handleConnectError = (error) => {
      console.error("❌ Home WebSocket connection error:", error);
    };

    const handleReconnect = (attemptNumber) => {
      console.log(
        "🔄 Home WebSocket reconnected after",
        attemptNumber,
        "attempts"
      );
      // Re-join live score room after reconnection
      socket.emit("joinLiveScore");
    };

    // Register event handlers
    webSocketManager.on("connect", handleConnect);
    webSocketManager.on("disconnect", handleDisconnect);
    webSocketManager.on("connect_error", handleConnectError);
    webSocketManager.on("reconnect", handleReconnect);

    setSocket(socket);

    return () => {
      webSocketManager.off("connect", handleConnect);
      webSocketManager.off("disconnect", handleDisconnect);
      webSocketManager.off("connect_error", handleConnectError);
      webSocketManager.off("reconnect", handleReconnect);
    };
  }, []);

  // Handle WebSocket messages for leaderboard updates
  useEffect(() => {
    if (!socket) return;

    // Listen for leaderboard updates
    const handleLeaderboardUpdate = (data) => {
      console.log("📊 Received leaderboard update:", data);
      if (data.teams) {
        setTeams(data.teams);
      }
    };

    // Listen for score updates that might affect leaderboard
    const handleScoreUpdate = () => {
      console.log("📊 Score update received, refreshing leaderboard");
      fetchTeams();
    };

    const handleSetEnd = () => {
      console.log("📊 Set end received, refreshing leaderboard");
      fetchTeams();
    };

    const handleMatchEnd = () => {
      console.log("📊 Match end received, refreshing leaderboard");
      fetchTeams();
    };

    const handleUndoScore = () => {
      console.log("📊 Undo score received, refreshing leaderboard");
      fetchTeams();
    };

    // Register event handlers
    socket.on("leaderboardUpdate", handleLeaderboardUpdate);
    socket.on("scoreUpdate", handleScoreUpdate);
    socket.on("setEnd", handleSetEnd);
    socket.on("matchEnd", handleMatchEnd);
    socket.on("undoScore", handleUndoScore);

    return () => {
      socket.off("leaderboardUpdate", handleLeaderboardUpdate);
      socket.off("scoreUpdate", handleScoreUpdate);
      socket.off("setEnd", handleSetEnd);
      socket.off("matchEnd", handleMatchEnd);
      socket.off("undoScore", handleUndoScore);
    };
  }, [socket]);

  const fetchTeams = async () => {
    try {
      console.log("displaySettings", displaySettings);
      const response = await fetch(API_ENDPOINTS.TEAMS_LEADERBOARD);
      const data = await response.json();
      if (data.success) {
        setTeams(data.data);
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMedalImage = (rank) => {
    switch (rank) {
      case 1:
        return Metal1;
      case 2:
        return Metal2;
      case 3:
        return Metal3;
      default:
        return null;
    }
  };

  const getRankBackgroundColor = (rank) => {
    switch (rank) {
      case 1:
        return "#EDB841"; // Gold
      case 2:
        return "#A3A5A8"; // Silver
      case 3:
        return "#D88143"; // Bronze
      default:
        return "#3D2817"; // Dark brown-gray
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-text">Loading Champions...</div>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      {/* Background texture overlay */}
      <div className="background-overlay"></div>

      <div className="content-wrapper">
        {/* Logo Section */}
        <div className="logo-section" style={{ display: "block" }}>
          <img
            src={Logo}
            alt="Logo"
            className="logo-image"
            style={{ width: "100%", maxWidth: "100%" }}
          />
        </div>

        {/* Hall of Champions Title */}
        <div className="hall-of-champions-title">
          <span className="star">✦</span>
          <h1>HALL OF CHAMPIONS</h1>
          <span className="star">✦</span>
        </div>

        {/* Leaderboard Section - Table Format */}
        <div className="leaderboard-section">
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th className="rank-header">Rank</th>
                <th className="team-header">Team</th>
                <th className="points-header">Team Point</th>
                <th className="points-header">Match Point</th>
                <th className="wins-header">Wins</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team, index) => {
                const rank = index + 1;
                const medalImage = getMedalImage(rank);

                return (
                  <tr
                    key={team.teamId}
                    className={`leaderboard-row rank-${rank}`}
                  >
                    <td className="rank-cell">
                      <div className="rank-content">
                        {medalImage && (
                          <img
                            src={medalImage}
                            alt={`Rank ${rank} medal`}
                            className="medal-icon-table"
                          />
                        )}
                        <span className="rank-number-table">#{rank}</span>
                      </div>
                    </td>
                    <td className="team-cell">
                      <div className="team-content">
                        {team.logo ? (
                          <img
                            src={team.logo}
                            alt={team.teamName}
                            className="team-logo"
                          />
                        ) : (
                          <div className="team-logo-placeholder">
                            {team.teamName.charAt(0)}
                          </div>
                        )}
                        <span className="team-name">{team.teamName}</span>
                      </div>
                    </td>
                    <td className="points-cell">
                      {team.teamPoint !== null && team.teamPoint !== undefined
                        ? team.teamPoint
                        : "-"}
                    </td>
                    <td className="points-cell">
                      {team.matchPoint !== null && team.matchPoint !== undefined
                        ? team.matchPoint
                        : "-"}
                    </td>
                    <td className="wins-cell">
                      {team.wins !== null && team.wins !== undefined
                        ? team.wins
                        : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Home;
